import React from 'react';
import {List, Map} from 'immutable';
import {constant, emptyObject, identity} from '@packages/utilities/fp/utils';
import {assoc} from '@packages/utilities/key-map';
import {removeCollectionSlugFromUrlPath} from '@packages/systems/core/utils/UrlUtils';
import {either} from '@packages/utilities/fp/result';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';
import {
  withSystem,
  Plugins,
  Expressions,
  StyleAttributeImm,
  Atom,
} from '@packages/systems/wfdl';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {encodeUriComponent} from '@packages/utilities/string-utils';
import {cleanHref, hrefAttr} from '@packages/systems/core/utils/HtmlUtils';
import {
  formatEmail,
  formatPhone,
} from '@packages/systems/dynamo/utils/DynamoFormattingUtils';
import {SHARED_ANCHOR_NODE_CONSTRAINTS_DESCENDANTS} from '@packages/systems/core/constants/SharedConstraintRules';
import {buildRteLinkAttrsForPreview} from '@packages/systems/core/utils/JsonDomUtils';
import {
  Environment,
  MODAL_TRIGGER_CLASS,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {EVENT_IDS_ATOM_FIELD} from '@packages/systems/optimization/constants';

import {
  TTypeConstructor,
  TText,
  TBoolean,
  TRecord,
  TTypeReference,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  type RenderContext,
  useIdAttr,
  useAsset,
  useCollectionIdToPageIdMap,
  useSite,
  usePreviewMode,
  useRuntimeEnvironment,
  useOptimizationClickMode,
} from '@packages/systems/render';
import {
  maybeAddEventTrackingAttribute,
  maybeAddEventIdsFromData,
} from '@packages/systems/optimization/utils';
import {
  getModalActionCommand,
  isModalAction,
  isPopoverAction,
  resolveEvaluatedOverlayTarget,
  type ModalAction,
  type PopoverAction,
} from '@packages/systems/overlays';
import {
  type EvaluatedNodeData,
  getArray,
  getBoolean,
  getRecord,
  getString,
  getStringOrElse,
} from '@packages/utilities/data-utils';
import {getEnclosingRichText} from './enclosingRichText';

const basicPlugin = Plugins.name('Basic');

const getIdAttrHref = (idAttr: string) => `#${idAttr}`;

const formatEmailWithSubject = (subject: string) => (email: any) =>
  formatEmail(email, subject, 'href');

const setBlankTarget = assoc('target')('_blank');
const withTarget = (linkData: Record<string, unknown>) =>
  getLinkField(linkData, 'target') ? setBlankTarget : identity;

const emailHref = either(constant(formatEmailWithSubject('')))(
  formatEmailWithSubject
);

const defaultLinkData: Record<string, unknown> = {
  mode: 'external',
  url: '',
  preload: 'none',
};

const getLinkField = (data: unknown, key: string): unknown =>
  getRecord(data)?.[key];

const getLinkData = (data: EvaluatedNodeData): Record<string, unknown> => {
  const link = getRecord(getLinkField(data, 'link'));
  return link || defaultLinkData;
};

const getLinkString = (
  linkData: Record<string, unknown>,
  key: string,
  defaultValue = ''
) => getStringOrElse(getLinkField(linkData, key), defaultValue);

/**
 * Re-derives a static collection-page link's own page path fresh via
 * collectionIdToPageIdMap/hrefMap — the same live lookup static "page" mode
 * links use, so it stays correct across locale and page renames — and keeps
 * the item's own slug segment from the stored snapshot (the picker doesn't
 * re-resolve that part per locale). Falls back to the stored snapshot
 * verbatim when the collection's page can't be resolved (e.g. a deleted
 * collection, or a legacy link saved before collectionId was stored).
 */
const resolveStaticDynPageSlug = (
  pageSlug: string,
  collectionId: string | undefined,
  collectionIdToPageIdMap: Record<string, string>,
  hrefMap: Record<string, string>
): string => {
  const pageId = collectionId
    ? collectionIdToPageIdMap[collectionId]
    : undefined;
  const collectionPagePath = pageId ? hrefMap[pageId] : undefined;
  const itemSlug = pageSlug.split('/').pop();
  return collectionPagePath && itemSlug
    ? `${collectionPagePath}/${itemSlug}`
    : pageSlug;
};

const buildAttrs = (
  idAttr: string,
  hrefMap: {
    [key: string]: string;
  },
  asset: RenderContext['assets'][string] | undefined,
  pageLinkHrefPrefix: string,
  data: EvaluatedNodeData,
  dataWHrefBindingPath:
    | ReadonlyArray<{type?: string; filter?: string}>
    | undefined,
  dynamoHref: undefined | string,
  hasBoundHref: boolean,
  runtimeEnvironment: Environment,
  collectionIdToPageIdMap: Record<string, string> = {}
): {
  [key: string]: string;
} => {
  const link = getLinkData(data);
  const linkMode = getLinkString(link, 'mode', 'external');
  const prefix = pageLinkHrefPrefix || '';

  if (hasBoundHref && !dynamoHref) {
    return emptyObject;
  }

  const dynHref = dynamoHref || '';

  if (linkMode === 'external') {
    const href = hasBoundHref ? dynHref : cleanHref(getLinkString(link, 'url'));
    return withTarget(link)(hrefAttr(href, runtimeEnvironment));
  }

  if (linkMode === 'page') {
    if (hasBoundHref) {
      return withTarget(link)(
        hrefAttr(`${prefix}${dynHref}`, runtimeEnvironment)
      );
    }
    const pageId = getString(getLinkField(link, 'pageId'));
    const slug = pageId ? hrefMap[pageId] : undefined;
    return slug
      ? withTarget(link)(hrefAttr(`${prefix}${slug}`, runtimeEnvironment))
      : emptyObject;
  }

  if (linkMode === 'dynpage') {
    if (hasBoundHref) {
      if (dataWHrefBindingPath && dataWHrefBindingPath.length > 0) {
        const part = getRecord(
          dataWHrefBindingPath[dataWHrefBindingPath.length - 1]
        );
        if (getString(getLinkField(part, 'type')) !== 'FullSlug') {
          // Start with "detailPage|collectionId"
          const [, collectionId] = getStringOrElse(
            getLinkField(part, 'filter'),
            'identity'
          ).split('|');
          // Then, map the collection ID to a page ID
          const pageId = collectionId
            ? collectionIdToPageIdMap[collectionId]
            : undefined;
          // Then, map the page ID to the path that it's mounted on (including folders)
          let pathToPage = pageId ? hrefMap[pageId] : undefined;
          if (pathToPage) {
            pathToPage = removeCollectionSlugFromUrlPath(pathToPage);

            return withTarget(link)(
              hrefAttr(`${prefix}${pathToPage}${dynHref}`, runtimeEnvironment)
            );
          }
        }
      }
      return withTarget(link)(
        hrefAttr(`${prefix}${dynHref}`, runtimeEnvironment)
      );
    }

    const pageSlug = getString(getLinkField(link, 'pageSlug'));
    if (pageSlug) {
      const resolvedSlug = resolveStaticDynPageSlug(
        pageSlug,
        getString(getLinkField(link, 'collectionId')),
        collectionIdToPageIdMap,
        hrefMap
      );
      return withTarget(link)(
        hrefAttr(`${prefix}${resolvedSlug}`, runtimeEnvironment)
      );
    }

    return emptyObject;
  }

  if (linkMode === 'section') {
    return hrefAttr(getIdAttrHref(idAttr), runtimeEnvironment);
  }

  if (linkMode === 'email') {
    const email = hasBoundHref ? dynHref : getLinkString(link, 'email');
    const subject = getLinkString(link, 'subject');
    const href = emailHref(encodeUriComponent(subject))(email);
    return href ? hrefAttr(href, runtimeEnvironment) : emptyObject;
  }

  if (linkMode === 'tel') {
    return hrefAttr(
      formatPhone(hasBoundHref ? dynHref : getLinkString(link, 'tel'), 'href'),
      runtimeEnvironment
    );
  }

  if (linkMode === 'file') {
    if (hasBoundHref) {
      return withTarget(link)(hrefAttr(dynHref, runtimeEnvironment));
    }

    const assetUrl = getString(getLinkField(link, 'assetUrl'));
    const fallback = assetUrl
      ? withTarget(link)(hrefAttr(assetUrl, runtimeEnvironment))
      : {};
    const getAttributesFromAsset = ({cdnUrl}: {cdnUrl: string}) =>
      withTarget(link)(hrefAttr(cdnUrl, runtimeEnvironment));
    return asset ? getAttributesFromAsset(asset) : fallback;
  }

  return emptyObject;
};

function isButtonElement(element: ExpressionElement) {
  return Expressions.getElementDataAt(
    element,
    [{in: 'Record', at: 'button'}],
    'Boolean',
    false
  );
}

const isButton = (data: EvaluatedNodeData) =>
  getBoolean(getLinkField(data, 'button'));
const isLinkToSelf = (
  attributes: Map<string, string | StyleAttributeImm | Map<string, string>>,
  pageHref: string | null | undefined
) => {
  return pageHref === attributes.get('href');
};

const maybeAddButton = (data: EvaluatedNodeData) => (classes: List<string>) => {
  return isButton(data) ? classes.push('w-button') : classes;
};

const maybeAddCurrent =
  (
    attributes: Map<string, string | StyleAttributeImm | Map<string, string>>,
    pageHref: string | null | undefined
  ) =>
  (classes: List<string>) => {
    return !attributes.has('hreflang') && isLinkToSelf(attributes, pageHref)
      ? classes.push('w--current')
      : classes;
  };

const maybeAddARIACurrentPage =
  (pageHref: string | null | undefined) =>
  (
    attributes: Map<string, string | StyleAttributeImm | Map<string, string>>
  ) => {
    return isLinkToSelf(attributes, pageHref)
      ? attributes.set('aria-current', 'page')
      : attributes;
  };

function isNonLocalHref(
  href: undefined | string | StyleAttributeImm | Map<string, string>
) {
  return typeof href === 'string' && !href.startsWith('#');
}

const PRELOAD_RENDER_ENVS = new Set<string>([
  WebflowEnvironments.SSR,
  WebflowEnvironments.EXPORT,
]);

const getPopoverAction = (action: unknown): PopoverAction =>
  isPopoverAction(action) ? action : 'toggle';

const getModalAction = (action: unknown): ModalAction =>
  isModalAction(action) ? action : 'show';

const Link = Atom.createRender((props) => {
  const {
    attributes,
    classes,
    data,
    context,
    generateAttributes,
    sharedContext: {pageHref, hrefMap},
    renderChild,
    innerHTML,
    href: dynHref,
    dataWHref,
    legacyDynamoBindingPaths,
    nativeIdPath,
    environmentContext,
  } = props;
  // Declaration-based (matches legacy `isBound`): bound if a path is declared, even before the value resolves.
  const hasBoundHref =
    Boolean(legacyDynamoBindingPaths?.href) ||
    Boolean(legacyDynamoBindingPaths?.dataWHref);
  const previewMode = usePreviewMode();
  // Dynamic rich text links need these attributes in order to work in previewMode
  // This is a temp fix until there's an easier way to make the rich text field's
  // jsonNode tree readily accessible while in Preview
  const runtimeEnvironment = useRuntimeEnvironment();
  const optimizationClickMode = useOptimizationClickMode();
  const inPreviewMode =
    previewMode && runtimeEnvironment === WebflowEnvironments.DESIGNER;
  const rteLinkAttributes =
    inPreviewMode && context.get('insideRichText')
      ? buildRteLinkAttrsForPreview(getRecord(data) || emptyObject)
      : {};
  const link = getLinkData(data);
  // dynpage reads the dataWHref bind path; it is always shallow, so narrow off the deep case.
  const boundDataWHrefPath = legacyDynamoBindingPaths?.dataWHref;
  const dataWHrefBindingPath = Array.isArray(boundDataWHrefPath)
    ? boundDataWHrefPath
    : undefined;
  const idAttr = useIdAttr(getLinkString(link, 'domId'));
  const asset = useAsset(getLinkString(link, 'assetId'));
  const collectionIdToPageIdMap = useCollectionIdToPageIdMap();
  const {pageLinkHrefPrefix} = useSite();
  const linkMode = getLinkString(link, 'mode', 'external');
  const isPopoverTrigger = linkMode === 'popover';
  const isModalTrigger = linkMode === 'modal';
  const isOverlayTrigger = isPopoverTrigger || isModalTrigger;
  const overlayTargetDomId = isOverlayTrigger
    ? resolveEvaluatedOverlayTarget(
        getLinkField(link, isModalTrigger ? 'modalTarget' : 'popoverTarget'),
        environmentContext.get('instanceIndexSuffix'),
        nativeIdPath,
        props.elementId,
        getEnclosingRichText(context)
      )
    : null;
  // The canvas renders Modals as manual popovers, so a canvas click must not
  // open one as a modal dialog. Only Preview and the published or exported
  // site get the native command.
  const rendersModalCommand =
    inPreviewMode ||
    runtimeEnvironment === WebflowEnvironments.SSR ||
    runtimeEnvironment === WebflowEnvironments.EXPORT;

  const linkAttributes = attributes.withMutations((_linkAttributes) => {
    if (isOverlayTrigger) {
      _linkAttributes
        .remove('href')
        .remove('target')
        .remove('rel')
        .remove('download')
        .remove('hreflang')
        .remove('aria-current')
        .set('type', 'button');
    }

    if (isModalTrigger) {
      if (overlayTargetDomId) {
        _linkAttributes.set('commandfor', overlayTargetDomId);
      } else {
        _linkAttributes.remove('commandfor');
      }

      if (rendersModalCommand) {
        _linkAttributes.set(
          'command',
          getModalActionCommand(
            getModalAction(getLinkField(link, 'modalAction'))
          )
        );
      } else {
        _linkAttributes.remove('command');
      }
    } else if (isPopoverTrigger) {
      _linkAttributes.set(
        'popovertargetaction',
        getPopoverAction(getLinkField(link, 'popoverAction'))
      );

      if (overlayTargetDomId) {
        _linkAttributes.set('popovertarget', overlayTargetDomId);
      } else {
        _linkAttributes.remove('popovertarget');
      }
    } else {
      _linkAttributes.merge(
        rteLinkAttributes,
        buildAttrs(
          idAttr,
          hrefMap,
          asset,
          pageLinkHrefPrefix,
          data,
          dataWHrefBindingPath,
          dynHref || dataWHref,
          hasBoundHref,
          runtimeEnvironment,
          collectionIdToPageIdMap
        )
      );
      _linkAttributes.update('href', (href) => href || '#');
      _linkAttributes.update(maybeAddARIACurrentPage(pageHref));
    }
    _linkAttributes.update(maybeAddEventIdsFromData(runtimeEnvironment, data));
    _linkAttributes.update(
      maybeAddEventTrackingAttribute(runtimeEnvironment, optimizationClickMode)
    );
  });

  const linkClasses = classes.withMutations((_linkClasses) => {
    _linkClasses.update(maybeAddButton(data));
    if (isPopoverTrigger) {
      _linkClasses.push('w-popover-trigger');
    } else if (isModalTrigger) {
      _linkClasses.push(MODAL_TRIGGER_CLASS);
    } else {
      _linkClasses.update(maybeAddCurrent(linkAttributes, pageHref));
    }
  });

  const preloadType = isOverlayTrigger
    ? 'none'
    : getLinkString(link, 'preload', 'none');
  const href = linkAttributes.get('href');
  // index/instanceIndex intentionally omitted (Link supplies no plugin context), matching the old renderChildren().
  const children = getArray(getRecord(data)?.children);
  const shouldRenderResource =
    preloadType !== 'none' &&
    isNonLocalHref(href) &&
    // Prevents preloading from rendering in designer and editor
    runtimeEnvironment &&
    PRELOAD_RENDER_ENVS.has(runtimeEnvironment);
  const LinkTag = isOverlayTrigger ? 'button' : 'a';

  return (
    <>
      <LinkTag {...generateAttributes(linkAttributes, linkClasses)}>
        {/* @ts-expect-error [PDTY-2706] - TS2322 - Type 'RichTextValue | VideoBindingProps | Iterable<number, ReactElement<EvalElementProps, string | JSXElementConstructor<any>>> | null' is not assignable to type 'ReactNode'. */}
        {
          // TODO (platform): fix innerHTML type issues
          innerHTML ||
            (children ? children.map((child) => renderChild(child)) : null)
        }
      </LinkTag>
      {shouldRenderResource && (
        // @ts-expect-error rel and href
        <link rel={preloadType} href={href} />
      )}
    </>
  );
});

const config = createDevlinkAtom({
  constraints: {
    descendants: SHARED_ANCHOR_NODE_CONSTRAINTS_DESCENDANTS,
  },
  displayName: {
    type: 'Derived',
    defaultValue: 'Link',
    getFromElement: (element) => {
      if (isButtonElement(element)) {
        return 'Button';
      } else if (
        Expressions.getElementDataAt(
          element,
          [{in: 'Record', at: 'block'}],
          'Text',
          ''
        ) === 'inline'
      ) {
        return 'Link Block';
      } else {
        return 'Link';
      }
    },
  },
  icon: {
    type: 'Derived',
    getFromElement: (element) =>
      isButtonElement(element)
        ? [basicPlugin, iconName('Button')]
        : [basicPlugin, iconName('Link')],
  },
  render: Link,
  showEmpty: true,
  fields: {
    button: withSystem({private: true}, TBoolean),
    block: withSystem({private: true}, TText),
    link: withSystem(
      {
        label: 'Link',
        isBindable: true,
        defaultValue: {mode: 'external'},
        isLocalizable: true,
      },
      TTypeConstructor(qualify(basicPlugin, typeName('Link')))
    ),
    eventIds: EVENT_IDS_ATOM_FIELD,
    children: withSystem(
      {labelVariant: 'hidden', label: 'Text'},
      TTypeConstructor(qualify(basicPlugin, typeName('LinkChildren')))
    ),
    attr: TRecord({}, TTypeReference('extraAttrs')),
  },
  // Add the native ID path in attributes for Analyze tracking
  autoEngagementTrackingAttribute: true,
});

export const forTestSuite = {
  buildAttrs,
};

export default config;
